
import { useState, useEffect, useMemo } from 'react'
import { reputationApi } from '../lib/api'
import { Badge, allBadges, getReputationLevel, getNextLevel, getLevelProgress, ReputationLevel } from '../mocks/badges'

interface UserStats {
  posts: number
  comments: number
  followers: number
  following: number
  reputation: number
  upvotesGiven: number
  groupsCreated: number
  reviewsPosted: number
  joinedAt: string | null
}

interface UseReputationReturn {
  badges: Badge[]
  unlockedBadges: Badge[]
  lockedBadges: Badge[]
  level: ReputationLevel
  nextLevel: ReputationLevel | null
  progress: number
  stats: UserStats
  loading: boolean
  totalBadges: number
  featuredBadge: Badge | null
}

const LAUNCH_DATE = new Date('2024-06-01')

export const useReputation = (userId?: string): UseReputationReturn => {
  const [stats, setStats] = useState<UserStats>({
    posts: 0,
    comments: 0,
    followers: 0,
    following: 0,
    reputation: 0,
    upvotesGiven: 0,
    groupsCreated: 0,
    reviewsPosted: 0,
    joinedAt: null,
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }
    fetchStats(userId)
  }, [userId])

  const fetchStats = async (uid: string) => {
    setLoading(true)
    try {
      // Fetch user stats via API
      const response = await reputationApi.getUserStats(uid)

      if (response.success && response.data) {
        setStats({
          posts: response.data.posts || 0,
          comments: response.data.comments || 0,
          followers: response.data.followers || 0,
          following: response.data.following || 0,
          reputation: response.data.reputation || 0,
          upvotesGiven: response.data.upvotesGiven || 0,
          groupsCreated: response.data.groupsCreated || 0,
          reviewsPosted: response.data.reviewsPosted || 0,
          joinedAt: response.data.joinedAt || null,
        })
      }
    } catch (err) {
      console.error('Error fetching reputation stats:', err)
    } finally {
      setLoading(false)
    }
  }

  const unlockedBadges = useMemo(() => {
    const earned: Badge[] = []

    // Posting badges
    if (stats.posts >= 1) earned.push(allBadges.find(b => b.id === 'first-post')!)
    if (stats.posts >= 10) earned.push(allBadges.find(b => b.id === 'prolific-writer')!)
    if (stats.posts >= 50) earned.push(allBadges.find(b => b.id === 'storyteller')!)
    if (stats.posts >= 100) earned.push(allBadges.find(b => b.id === 'drama-chronicler')!)

    // Engagement badges
    if (stats.comments >= 1) earned.push(allBadges.find(b => b.id === 'first-comment')!)
    if (stats.comments >= 25) earned.push(allBadges.find(b => b.id === 'active-commenter')!)
    if (stats.comments >= 100) earned.push(allBadges.find(b => b.id === 'discussion-leader')!)
    if (stats.comments >= 500) earned.push(allBadges.find(b => b.id === 'community-pillar')!)

    // Social badges
    if (stats.followers >= 1) earned.push(allBadges.find(b => b.id === 'friendly-face')!)
    if (stats.followers >= 25) earned.push(allBadges.find(b => b.id === 'popular-member')!)
    if (stats.followers >= 100) earned.push(allBadges.find(b => b.id === 'community-star')!)
    if (stats.followers >= 500) earned.push(allBadges.find(b => b.id === 'influencer')!)

    // Reputation badges
    if (stats.reputation >= 50) earned.push(allBadges.find(b => b.id === 'rising-star')!)
    if (stats.reputation >= 200) earned.push(allBadges.find(b => b.id === 'trusted-member')!)
    if (stats.reputation >= 500) earned.push(allBadges.find(b => b.id === 'drama-authority')!)
    if (stats.reputation >= 1000) earned.push(allBadges.find(b => b.id === 'legendary-status')!)

    // Special badges
    if (stats.joinedAt && new Date(stats.joinedAt) <= LAUNCH_DATE) {
      earned.push(allBadges.find(b => b.id === 'early-adopter')!)
    }
    if (stats.reviewsPosted >= 5) earned.push(allBadges.find(b => b.id === 'drama-reviewer')!)
    if (stats.groupsCreated >= 1) earned.push(allBadges.find(b => b.id === 'group-creator')!)
    if (stats.upvotesGiven >= 50) earned.push(allBadges.find(b => b.id === 'helpful-voter')!)

    return earned.filter(Boolean)
  }, [stats])

  const lockedBadges = useMemo(() => {
    const unlockedIds = new Set(unlockedBadges.map(b => b.id))
    return allBadges.filter(b => !unlockedIds.has(b.id))
  }, [unlockedBadges])

  const level = useMemo(() => getReputationLevel(stats.reputation), [stats.reputation])
  const nextLevel = useMemo(() => getNextLevel(stats.reputation), [stats.reputation])
  const progress = useMemo(() => getLevelProgress(stats.reputation), [stats.reputation])

  const featuredBadge = useMemo(() => {
    if (unlockedBadges.length === 0) return null
    const tierOrder = ['legendary', 'platinum', 'gold', 'silver', 'bronze']
    for (const tier of tierOrder) {
      const badge = unlockedBadges.find(b => b.tier === tier)
      if (badge) return badge
    }
    return unlockedBadges[0]
  }, [unlockedBadges])

  return {
    badges: allBadges,
    unlockedBadges,
    lockedBadges,
    level,
    nextLevel,
    progress,
    stats,
    loading,
    totalBadges: allBadges.length,
    featuredBadge,
  }
}
